import { useMemo } from 'react';
import { FormGroup, FormLabel } from 'react-bootstrap';
import ReactSelect, { StylesConfig } from 'react-select';
import { useTheme } from 'styled-components';

interface CustomSelectSearchableProps {
  id?: string;
  value?: string | number;
  title: string;
  disabled?: boolean;
  name: string;
  onChange?: (value: any) => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  options: { value: any; label: string }[];
  style?: React.CSSProperties;
  required?: boolean;
  placeholder?: string;
  'data-testid'?: boolean | string;
}

export default function CustomSelectSearchable({
  id,
  value,
  title,
  disabled,
  name,
  onChange,
  options,
  required,
  placeholder = 'Selecione ou digite para buscar...',
  'data-testid': dataTestId,
}: CustomSelectSearchableProps) {
  const theme = useTheme();

  const controlId = id ?? name;

  const testId = useMemo(() => {
    if (typeof dataTestId === 'string') return dataTestId;
    return dataTestId ? `input-selectsearch-${controlId}` : undefined;
  }, [dataTestId, controlId]);

  // Encontra a opção selecionada
  const selectedOption = options.find((opt) => opt.value === value) || null;

  // Handler para onChange - converte para o formato esperado
  const handleChange = (selectedOption: any) => {
    if (onChange) {
      // Simula um evento de change do select nativo
      const syntheticEvent = {
        target: {
          name,
          value: selectedOption?.value || '',
        },
      } as React.ChangeEvent<HTMLSelectElement>;

      onChange(syntheticEvent);
    }
  };

  // Estilos customizados para o react-select
  const customStyles: StylesConfig = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: '10px',
      border: `1px solid ${theme.colors.bordaInput}`,
      minHeight: '38px',
      boxShadow: state.isFocused
        ? `0 0 0 0.2rem ${theme.colors.azulgov}40`
        : 'none',
      '&:hover': {
        borderColor: theme.colors.azulgov,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.colors.azulgov
        : state.isFocused
          ? theme.colors.fundo
          : 'white',
      color: state.isSelected ? 'white' : theme.colors.textoComum,
      cursor: 'pointer',
      padding: '10px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: '300px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '300px',
    }),
  };

  return (
    <FormGroup>
      <FormLabel
        style={{ color: theme.colors.textoAzul }}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {required ? (
          <>
            <span
              style={{ color: theme.colors.botaoConfirmar, fontWeight: 'bold' }}
            >
              {title}
            </span>
            <span style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}>
              {' *'}
            </span>
          </>
        ) : (
          title
        )}
      </FormLabel>
      <div data-testid={testId}>
        <ReactSelect
          name={name}
          value={selectedOption}
          onChange={handleChange}
          options={options}
          isDisabled={disabled}
          isClearable
          isSearchable
          placeholder={placeholder}
          styles={customStyles}
          noOptionsMessage={() => 'Nenhuma opção encontrada'}
          loadingMessage={() => 'Carregando...'}
          data-testid={dataTestId}
          // Melhora a performance para grandes listas
          filterOption={(option, inputValue) => {
            // Busca case-insensitive
            return option.label
              .toLowerCase()
              .includes(inputValue.toLowerCase());
          }}
        />
      </div>
    </FormGroup>
  );
}
